import {
  ChangeDetectorRef,
  computed,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  Renderer2,
  untracked,
} from '@angular/core';

import {
  icon,
  IconLookup,
  IconName,
  IconParams,
  IconPrefix,
  layer,
  parse,
  text,
} from '@fortawesome/fontawesome-svg-core';

import {
  IconMapping,
  iconMappings,
  IconType,
} from '../constants/icon-mappings';
import { isNil } from 'src/utils/utils';

/**
 * IconDirective. This directive shows icons on html elements.
 *
 * @param {string} icon The icon name that can be shown or that can be mapped to it's corresponding icon.
 * @param {IconMapping} iconMappings Mappings to be added or overwritten by the directive. Used when the input icon name needs to be mapped
 * to a different icon name. If no value is provided `iconMappings` from
 * `@wettermanufaktur-gmbh/ui-sdk/constants/icon-mappings` will be used.
 * @param {IconPrefix} fontAwesomeStyle `IconPrefix` for the main icon.
 * @param {string[]} additionalClasses Classes that can be added to the layer. E.g. for coloring.
 * @param {{ [key: string]: string }} mainIconStyles Custom styles for main icon. E.g. {'--fa-primary-color': 'red'}.
 * @param {string} undefinedIconText Text to display if icon does not exist.
 */
@Directive({
  selector: '[appIcon]',
  standalone: true,
})
export class IconDirective {
  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  private ref = inject(ChangeDetectorRef);

  iconName = input.required<string>();
  iconMappings = input<IconMapping | undefined>();

  fontAwesomeStyle = input<IconPrefix>('far');
  additionalClasses = input<string[] | undefined>(undefined);
  undefinedIconText = input<string | undefined>(undefined);
  mainIconStyles = input<{ [key: string]: string } | undefined>(undefined);

  _iconName = computed(() => {
    const iconNameValue = this.iconName();
    if (iconNameValue?.substring(0, 2) === 'rr') {
      return 'rr';
    } else {
      return iconNameValue;
    }
  });
  _iconMappings = computed(() => {
    const iconMappingsValue = this.iconMappings();
    if (isNil(iconMappingsValue)) return iconMappings;
    else return { ...iconMappings, ...iconMappingsValue };
  });

  _createdIcon: any;

  get iconDefinition() {
    return (
      this._iconMappings()[this._iconName()] ?? {
        mainIcon: this._iconName() as IconName,
        type: undefined,
        additionalIcon: undefined,
        text: undefined,
        text_transform: undefined,
        mainIcon_transform: undefined,
        mainIcon_style: undefined,
        additionalIcon_transform: undefined,
        additionalIcon_style: undefined,
      }
    );
  }

  get isIconDefined() {
    return !isNil(this.iconDefinition);
  }

  get isIndexIcon() {
    return this.iconDefinition?.type === 'index';
  }

  get hasMainIcon() {
    return !isNil(this.iconDefinition?.mainIcon);
  }

  get hasAdditionalIcon() {
    return !isNil(this.iconDefinition?.additionalIcon);
  }

  get hasTextIcon() {
    return !isNil(this.iconDefinition?.text);
  }

  constructor() {
    effect(() => {
      this.iconName();
      this.iconMappings();
      this.additionalClasses();
      this.mainIconStyles();
      untracked(() => {
        this.resetIcon();
        this.ref.markForCheck();
      });
    });
  }

  private resetIcon() {
    this.removeIcon();
    this.createIcon();
  }

  private createIcon() {
    const parentNode = this.renderer.parentNode(this.elementRef.nativeElement);
    const appendingElements = [] as any[];

    if (this.isIconDefined) {
      if (this.isIndexIcon) {
        const iconConfig = this.createIconConfig(IconType.MAIN_ICON);
        iconConfig.iconParams.transform = {
          ...iconConfig.iconParams.transform,
          ...parse.transform('shrink-2'),
        };
        const additionalIconStyle =
          this.iconDefinition!.additionalIcon_style ?? this.fontAwesomeStyle();

        const mainIcon = icon(iconConfig.iconLookUp, {
          ...iconConfig.iconParams,
          styles: {
            ...(iconConfig.iconParams.styles ?? {}),
            ...(this.mainIconStyles() ?? {}),
          },
        });
        const additionalIcon = icon(
          {
            prefix: additionalIconStyle,
            iconName: this.iconDefinition!.additionalIcon!,
          },
          { transform: parse.transform('grow-11') }
        );

        if (!isNil(mainIcon) && !isNil(additionalIcon)) {
          appendingElements.push(mainIcon, additionalIcon);
        }
      } else if (
        this.hasMainIcon &&
        !this.hasAdditionalIcon &&
        !this.hasTextIcon
      ) {
        const iconConfig = this.createIconConfig(IconType.MAIN_ICON);
        const mainIcon = icon(iconConfig.iconLookUp, {
          ...iconConfig.iconParams,
          styles: {
            ...(iconConfig.iconParams.styles ?? {}),
            ...(this.mainIconStyles() ?? {}),
          },
        });
        if (!isNil(mainIcon)) {
          appendingElements.push(mainIcon);
        }
      } else if (
        this.hasMainIcon &&
        this.hasAdditionalIcon &&
        !this.hasTextIcon
      ) {
        const iconConfig = this.createIconConfig(IconType.MAIN_ICON);

        const additionalIconConfig = this.createIconConfig(
          IconType.ADDITIONAL_ICON
        );

        const mainIcon = icon(iconConfig.iconLookUp, {
          ...iconConfig.iconParams,
          styles: {
            ...(iconConfig.iconParams.styles ?? {}),
            ...(this.mainIconStyles() ?? {}),
          },
        });
        const additionalIcon = icon(
          additionalIconConfig.iconLookUp,
          additionalIconConfig.iconParams
        );

        if (!isNil(mainIcon) && !isNil(additionalIcon)) {
          appendingElements.push(mainIcon, additionalIcon);
        }
      } else if (
        this.hasMainIcon &&
        !this.hasAdditionalIcon &&
        this.hasTextIcon
      ) {
        const iconConfig = this.createIconConfig(IconType.MAIN_ICON);

        const text = this.createText(
          this.iconDefinition!.text!,
          this.iconDefinition!.text_transform
        );

        const mainIcon = icon(iconConfig.iconLookUp, {
          ...iconConfig.iconParams,
          styles: {
            ...(iconConfig.iconParams.styles ?? {}),
            ...(this.mainIconStyles() ?? {}),
          },
        });

        if (!isNil(mainIcon) && !isNil(text)) {
          appendingElements.push(mainIcon, text);
        }
      } else if (
        this.hasMainIcon &&
        this.hasAdditionalIcon &&
        this.hasTextIcon
      ) {
        const iconConfig = this.createIconConfig(IconType.MAIN_ICON);
        const additionalIconConfig = this.createIconConfig(
          IconType.ADDITIONAL_ICON
        );

        const text = this.createText(
          this.iconDefinition!.text!,
          this.iconDefinition!.text_transform
        );
        const mainIcon = icon(iconConfig.iconLookUp, {
          ...iconConfig.iconParams,
          styles: {
            ...(iconConfig.iconParams.styles ?? {}),
            ...(this.mainIconStyles() ?? {}),
          },
        });
        const additionalIcon = icon(
          additionalIconConfig.iconLookUp,
          additionalIconConfig.iconParams
        );

        if (!isNil(mainIcon) && !isNil(additionalIcon) && !isNil(text)) {
          appendingElements.push(mainIcon, additionalIcon, text);
        }
      }
    }

    if (appendingElements.length > 0) {
      this._createdIcon = layer(
        push => {
          appendingElements.map(el => push(el));
        },
        {
          classes: this.additionalClasses() ?? ['fa-fw'],
        }
      ).node[0];

      this.renderer.insertBefore(
        parentNode,
        this._createdIcon,
        parentNode.firstChild
      );
    } else {
      this.createUndefinedIcon(parentNode);
    }
  }

  private createUndefinedIcon(parentNode: any) {
    if (isNil(this.undefinedIconText())) {
      const iconConfig = this.createIconConfig(IconType.UNDEFINED_ICON);
      const undefinedIcon = icon(iconConfig.iconLookUp, iconConfig.iconParams);
      this._createdIcon = layer(
        push => {
          push(undefinedIcon);
        },
        { classes: this.additionalClasses() ?? ['fa-fw'] }
      ).node[0];
    } else {
      const text = this.createText(this.undefinedIconText()!);
      this._createdIcon = layer(
        push => {
          push(text);
        },
        { classes: this.additionalClasses() ?? ['fa-fw'] }
      ).node[0];
    }

    this.renderer.insertBefore(
      parentNode,
      this._createdIcon,
      parentNode.firstChild
    );
  }

  private removeIcon() {
    if (!isNil(this._createdIcon)) {
      const parentNode = this.renderer.parentNode(
        this.elementRef.nativeElement
      );
      this.renderer.removeChild(parentNode, this._createdIcon);
    }
  }

  private createIconConfig(iconToCreate: IconType) {
    const faStyle =
      this.iconDefinition?.mainIcon_style ?? this.fontAwesomeStyle();

    const iconLookUp = {
      prefix: faStyle,
    } as IconLookup;
    const iconParams = {} as IconParams;

    if (!isNil(this.iconDefinition?.mainIcon_transform)) {
      iconParams.transform = parse.transform(
        this.iconDefinition!.mainIcon_transform
      );
    }

    if (iconToCreate === IconType.MAIN_ICON) {
      iconLookUp.iconName = this.iconDefinition!.mainIcon;
    } else if (iconToCreate === IconType.ADDITIONAL_ICON) {
      const additionalIconStyle =
        this.iconDefinition!.additionalIcon_style ?? this.fontAwesomeStyle();
      iconLookUp.iconName = this.iconDefinition!.additionalIcon!;
      iconLookUp.prefix = additionalIconStyle;
      if (!isNil(this.iconDefinition!.additionalIcon_transform)) {
        iconParams.transform = {
          ...iconParams.transform,
          ...parse.transform(this.iconDefinition!.additionalIcon_transform!),
        };
      }
    } else if (iconToCreate === IconType.UNDEFINED_ICON) {
      iconLookUp.iconName = 'circle-question';
      iconLookUp.prefix = this.fontAwesomeStyle();
      iconParams.classes = ['fa-beat-fade'];
    }
    return { iconLookUp, iconParams };
  }

  private createText(inputText: string, text_transform?: string) {
    return text(inputText, {
      transform: text_transform ? parse.transform(text_transform) : {},
    });
  }
}
